import React from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Card, Col, Row } from 'reactstrap';
import { CalibrationItem, CalibrationOriginalItem, Question } from 'types';
import { formatQuestion } from 'utils/custom-question/formatter';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Avatar from 'views/Widgets/People/Avatar';

interface Props {
  calibrationItems: CalibrationItem[];
  question: Question;
  originalResponses: CalibrationOriginalItem[];
}

const CalibrationHistoryItems: React.FC<Props> = ({
  calibrationItems,
  question,
  originalResponses,
}) => {
  const { formatMessage } = useIntl();
  return (
    <>
      <div className="pb-3 text-muted text-uppercase">
        <FormattedMessage
          id="app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.calibration_history"
          defaultMessage="Calibration history"
        />
      </div>
      {calibrationItems.length === 0 && (
        <div className="text-muted">
          <FormattedMessage
            id="app.team_dashboard.calibration.no_history"
            defaultMessage="No calibration history available."
          />
        </div>
      )}
      {calibrationItems.map(
        (calibrationItem: CalibrationItem, index: number) => (
          <Card key={index} className="p-2 mb-3">
            <Row className="align-items-center">
              <Col className="ps-4 col-1">
                <Avatar size="xs" person={calibrationItem.author} />
              </Col>
              <Col className="col-2 col-sm-3">
                <span className="text-muted small ms-2 text-nowrap">
                  <FormattedDate value={calibrationItem.created_at} />
                </span>
              </Col>
              <Col className="col-auto">
                <span className="fw-bold ps-1">
                  {formatQuestion(
                    question,
                    calibrationItem.value,
                    formatMessage
                  )}
                </span>
              </Col>
            </Row>
            <Row className="align-items-center mt-2 pt-2">
              <Col className="col-12">
                <div
                  className={'comment-body d-block px-3'}
                  style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                >
                  {calibrationItem?.comment ? (
                    <RichTextViewer
                      expanded={true}
                      model={calibrationItem.comment}
                    />
                  ) : (
                    <FormattedMessage
                      id="app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.no_comment_provided"
                      defaultMessage="No comment provided."
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        )
      )}
      {originalResponses?.length > 0 && (
        <>
          <hr />
          <div className="pb-3">
            <span className="text-muted text-uppercase">
              <FormattedMessage
                id="app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.original_answer"
                defaultMessage="Original {count, plural, one {answer} other {answers}}"
                values={{ count: originalResponses.length }}
              />
            </span>
          </div>
          {originalResponses.map((originalResponse, idx) => (
            <Card key={idx} className="p-2 mb-2 mb-4">
              <Row className="align-items-center">
                <Col className="ps-4 col-1">
                  <Avatar size="xs" person={originalResponse.author} />
                </Col>
                <Col>
                  <span className="fw-bold ps-2 ms-2">
                    {formatQuestion(
                      question,
                      originalResponse.value,
                      formatMessage
                    )}
                  </span>
                </Col>
              </Row>
              {!!originalResponse?.comment && (
                <Row className="align-items-center mt-2 pt-2">
                  <Col className="col-12">
                    <div
                      className={'comment-body d-block px-3'}
                      style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                    >
                      <RichTextViewer
                        expanded={true}
                        model={originalResponse.comment}
                      />
                    </div>
                  </Col>
                </Row>
              )}
            </Card>
          ))}
        </>
      )}
    </>
  );
};

export default React.memo(CalibrationHistoryItems);
